/* eslint-disable max-lines */
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { type ApiResponse } from "@src/appV2/api";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockInChartingReminder } from "@src/appV2/redesign/Charting/ClockInChartingReminder";
import { YourChartingLoginBottomSheetContainer } from "@src/appV2/redesign/Charting/YourChartingLoginBottomSheetContainer";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH,
  SHIFT_DETAILS_TIMEKEEPING_FULL_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { type ExternalTimekeepingIntegrationsConfig } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  type RecordTimekeepingActionResponse,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { InternalTimeClockTutorialBottomSheet } from "../TimekeepingIntegrations/InternalTimeClockTutorialBottomSheet";
import { UseInternalTimeClockContent } from "../TimekeepingIntegrations/UseInternalTimeClockContent";
import { ActionNotFoundContent } from "./ActionNotFoundContent";
import { ActionSkippedContent } from "./ActionSkippedContent";
import {
  DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
} from "./constants";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInTimekeepingIntegrationBottomSheetProps {
  modalState: UseModalState;
  config: ExternalTimekeepingIntegrationsConfig;
  shiftId: string;
  shiftStartDate: Date;
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  breakPolicyNoteId: string;
  requiresBreakPolicyAcknowledgement: boolean;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  chartingReminderEnabled: boolean;
  chartingCredentialSharingIsEnabled: boolean;
  chartingCredential?: ChartingCredential;
  targetArrivalTime?: Date;
  timezone?: string;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  shiftBookedAt?: Date;
  workerStage: WorkerStage;
  workerSubstage?: string;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
}

type ClockInTimekeepingIntegrationBottomSheetStep =
  | "use-time-clock"
  | "action-not-found"
  | "action-skipped"
  | "requesting-clock-in"
  | "requesting-non-instant-pay"
  | "clock-in-success"
  | "workplace-rules"
  | "charting-reminder";

export function ClockInTimekeepingIntegrationBottomSheet(
  props: ClockInTimekeepingIntegrationBottomSheetProps
) {
  const {
    modalState,
    config,
    shiftId,
    shiftStartDate,
    workerId,
    workplaceId,
    workplaceName,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    chartingReminderEnabled,
    chartingCredentialSharingIsEnabled,
    chartingCredential,
    earliestGeofenceEntryTime,
    targetArrivalTime,
    timezone,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    shiftBookedAt,
    workerStage,
    workerSubstage,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
  } = props;

  const history = useHistory();

  const chartingReminderModalState = useModalState();
  const chartingCredentialsModalState = useModalState();
  const internalTimeClockTutorialModalState = useModalState();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const firstStep: ClockInTimekeepingIntegrationBottomSheetStep = isDefined(config.badgeNumber)
    ? "use-time-clock"
    : "action-not-found";
  const [step, setStep] = useState<ClockInTimekeepingIntegrationBottomSheetStep>(firstStep);

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { wait, skip } = useSkippableWait();

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const shiftTimekeepingPath = generatePath(SHIFT_DETAILS_TIMEKEEPING_FULL_PATH, {
    shiftId,
  });

  const clockInTime = postClockInShift?.data.clockInOut?.start
    ? parseISO(postClockInShift.data.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate,
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    modalState.closeModal();
    setStep(firstStep);
  }, [firstStep, modalState, skip]);

  const completeSuccessFlow = useCallback(
    async (postMutationShift?: ApiResponse<RecordTimekeepingActionResponse>) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      if (postMutationShift) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: postMutationShift.data.clockInOut,
                },
              }
            : undefined
        );

        history.replace(shiftTimekeepingPath);
      }
    },
    [wait, queryClient, shiftId, history, shiftTimekeepingPath]
  );

  const onClockInSuccess = useCallback(
    async (postMutationShift: ApiResponse<RecordTimekeepingActionResponse>) => {
      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else if (chartingReminderEnabled) {
        setStep("charting-reminder");
        chartingReminderModalState.openModal();
      } else {
        await completeSuccessFlow(postMutationShift);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      chartingReminderEnabled,
      chartingReminderModalState,
    ]
  );

  async function recordClockIn() {
    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        shiftActionCheck: ActionCheckType.INTEGRATION,
      });

      await onClockInSuccess(postMutationShift);
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.message === TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE
      ) {
        setStep("action-not-found");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockInType: "timekeeping-integration",
        error: errorMessage,
      });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.EXTERNAL_INTEGRATION_FAILURE,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  return (
    <>
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          action={NoteAcknowledgementAction.CLOCK_IN}
          isLoading={false}
          modalState={modalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId}
          onAcknowledge={async () => {
            setRequiresBreakPolicyAcknowledgement(false);
          }}
        />
      )}

      {!requiresBreakPolicyAcknowledgement && (
        <BottomSheet modalState={modalState} canDismiss={false}>
          {step === "use-time-clock" && isDefined(config.badgeNumber) && (
            <UseInternalTimeClockContent
              action="clock-in"
              badgeNumber={config.badgeNumber}
              pinsToEnter={config.pinsToEnterString}
              timeclockLocation={config.timeclockLocationString}
              onActionConfirm={async () => {
                setStep("requesting-clock-in");
                await recordClockIn();
              }}
              onHelp={() => {
                internalTimeClockTutorialModalState.openModal();
              }}
              onCancel={abortFlowAndClose}
            />
          )}
          {step === "action-not-found" && (
            <ActionNotFoundContent
              action="clock-in"
              onTryAgain={
                isDefined(config.badgeNumber)
                  ? async () => {
                      setStep("requesting-clock-in");
                      await recordClockIn();
                    }
                  : undefined
              }
              onSkipAction={() => {
                setStep("action-skipped");
              }}
              onClose={isDefined(config.badgeNumber) ? undefined : abortFlowAndClose}
            />
          )}
          {step === "action-skipped" && (
            <ActionSkippedContent
              action="clock-in"
              onAccept={async () => {
                setStep("requesting-non-instant-pay");
                await convertToNonInstantPay();
              }}
              onCancel={() => {
                if (isDefined(config.badgeNumber)) {
                  setStep("use-time-clock");
                } else {
                  abortFlowAndClose();
                }
              }}
            />
          )}
          {step === "requesting-clock-in" && (
            <BottomSheetLoadingContent
              title="Confirming your clock in"
              description="Should only take a few seconds"
            />
          )}
          {step === "clock-in-success" && (
            <TimekeepingActionSuccessContent
              title="Have a great shift!"
              description={getClockInSuccessDescription({
                hasWorkerClockedInOnTime,
                isSecondShiftInDouble,
                earliestGeofenceEntryTime,
                isTimesheetEditDisabled,
                shouldEnforceNfcTimesheetBounds,
                isArrivalTimeFeatureEnabled,
                targetArrivalTime,
                shiftStartDate,
                clockInTime,
                timezone,
                shiftBookedAt,
                policy: clipboardScoreProfile?.policy,
                workerStage,
                workerSubstage,
                isClipboardScoreFreezeEnabled,
                clipboardScoreFreezeReason,
              })}
              onClose={abortFlowAndClose}
            />
          )}
          {step === "workplace-rules" && workplaceRules && (
            <WorkplaceRulesReminderContent
              shiftId={shiftId}
              workplaceId={workplaceId}
              workplaceRules={workplaceRules}
              onContinue={async () => {
                if (chartingReminderEnabled) {
                  setStep("charting-reminder");
                  chartingReminderModalState.openModal();
                } else {
                  await completeSuccessFlow(postClockInShift);
                }
              }}
            />
          )}
          {step === "charting-reminder" && (
            <ClockInChartingReminder
              chartingReminderModalState={chartingReminderModalState}
              chartingCredentialsModalState={chartingCredentialsModalState}
              shiftId={shiftId}
              workplaceId={workplaceId}
              chartingCredential={chartingCredential}
              chartingCredentialSharingIsEnabled={chartingCredentialSharingIsEnabled}
              onClose={async () => {
                await completeSuccessFlow(postClockInShift);
              }}
            />
          )}
          {step === "requesting-non-instant-pay" && (
            <BottomSheetLoadingContent
              title="Converting to non-instant pay"
              description="Should only take a few seconds"
            />
          )}
        </BottomSheet>
      )}

      <YourChartingLoginBottomSheetContainer
        modalState={chartingCredentialsModalState}
        chartingCredentials={chartingCredential}
        shiftId={shiftId}
        workplaceId={workplaceId}
        workerId={workerId}
        onClose={async () => {
          await completeSuccessFlow(postClockInShift);
        }}
      />

      {isDefined(config.badgeNumber) ? (
        <InternalTimeClockTutorialBottomSheet
          action="clock-in"
          modalState={internalTimeClockTutorialModalState}
          steps={config.tutorial.stepsClockIn}
          placeholders={{ workplaceName, badgeNumber: config.badgeNumber }}
          onTimeclockActionPerformed={async () => {
            internalTimeClockTutorialModalState.closeModal();

            setStep("requesting-clock-in");
            await recordClockIn();
          }}
          onTimeclockNotWorking={() => {
            internalTimeClockTutorialModalState.closeModal();
            setStep("action-skipped");
          }}
        />
      ) : null}
    </>
  );
}
/* eslint-enable max-lines */
